import { CommonModule } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { finalize, forkJoin, Subscription, take } from 'rxjs';
import { DataTableComponent } from 'src/app/components/data-table/data-table.component';
import { InlineActionComponent } from 'src/app/components/data-table/inline-action/inline-action.component';
import { LoadingIconComponent } from 'src/app/components/loading-icon/loading-icon.component';
import { FormDialogService } from 'src/app/services/form-dialog.service';
import { IconModule } from 'src/app/icon/icon.module';
import { MaterialModule } from 'src/app/material.module';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { ProcessService } from 'src/app/services/process.service';
import { ToastService } from 'src/app/services/toast.service';
import { AppConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { DIALOG_WIDTH, ROUTES } from 'src/app/shared/constants';
import { FilterType, TableDataType, TableLayout } from 'src/app/shared/enums';
import { IConfirmDialog } from 'src/app/shared/interfaces/IDialog';
import { IColumnConfig, IDataSourceFilterTable, IRetrieveParam, ISelectionOption, ITableConfig, ITopOptions } from 'src/app/shared/interfaces/ITable';
import { IImportProcessError, ImportProcessDialogComponent, ImportProcessDialogResult } from './import-process-dialog/import-process-dialog.component';
import { ImportProcessErrorsComponent } from './import-process-errors/import-process-errors.component';
import { AddDatasetFormComponent } from './add-dataset-form/add-dataset-form.component';
import { SystemService } from 'src/app/services/system.service';

@Component({
  selector: 'app-process',
  imports: [DataTableComponent, MaterialModule, ImportProcessErrorsComponent],
  templateUrl: './process.component.html',
  styleUrls: ['./process.component.scss']
})
export class ProcessComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('processTable') processTable: DataTableComponent;
  @ViewChild('importErrorsSection') importErrorsSection?: ElementRef<HTMLElement>;
  importErrors: IImportProcessError[] = [];
  tableConfig: ITableConfig;
  topOption: ITopOptions;
  selectionOptions: any;
  unsubscribe: Subscription[] = [];
  params: IRetrieveParam = { sortText: 'id.desc', page: 1, pageSize: 10 };
  dataSourceFilterOptions: IDataSourceFilterTable[] = [];
  datasetUnitFilterOptions: IDataSourceFilterTable[] = [];

  constructor(
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private router: Router,
    private translateService: TranslateService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private formDialog: FormDialogService,
    private authService: AuthService,
    private systemService: SystemService,
    private processService: ProcessService) { }

  get isAdmin(): boolean {
    return this.router.url.startsWith('/admin');
  }

  get disable() {
    return this.processTable?.loading ?? false;
  }

  ngOnInit(): void {
    this.selectionOptions = this.getSelectionOptions();

    this.tableConfig = {
      columnConfig: this.initColumnConfig(),
      layout: TableLayout.MODERN,
      title: 'pef database',
      description: this.isAdmin ? 'pef database description' : 'pef database description user',
      selectionLabel: 'datasets selected'
    };
    this.loadFilterOptions();

    const hideExport = !this.isAdmin && !this.authService.userInfo?.hasPefDatabaseAccess;

    this.topOption = {
      showSearch: true,
      topActions: [
        {
          name: 'process export',
          action: () => { },
          hidden: () => hideExport,
          customComponent: {
            component: ExportProcessComponent,
            inputs: {
              table: () => this.processTable,
              isAdmin: this.isAdmin
            }
          }
        },
        {
          name: () => 'import',
          icon: 'cloud-upload',
          action: () => this.openImportDialog(),
          hidden: () => !this.isAdmin || this.processTable?.loading
        },
        {
          name: 'delete',
          icon: 'trash',
          action: () => this.handleMultipleDelete(),
          hidden: () => !this.isAdmin || !this.hasSelectedItems(),
          selectionAction: true,
          class: 'selection-action-danger'
        },
        {
          name: 'add',
          hidden: () => !this.isAdmin,
          action: () => {
            this.openAddProcessDialog();
          }
        } 
      ]
    };

    this.unsubscribe.push(
      this.processService.retrieveProcessTable$.subscribe((value) => {
        if (value) this?.retrieveData();
      })
    );
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  handleDeleteProcess = (id: number) => {
    this.toastService.confirmDelete({
      title: 'delete dataset',
      content: 'do you want to delete this dataset',
      confirmText: 'delete',
      width: DIALOG_WIDTH.SMALLEST,
      onConfirm: () => {
        this.loadingService.show();
        this.processService.deleteProcess([id]).pipe(finalize(() => this.loadingService.hide())).subscribe({
          next: () => {
            this.toastService.success(this.translateService.instant('delete successfully'));
            this.retrieveData();
          },
          error: (error) => console.error(error)
        });
      }
    });
  };

  handleMultipleDelete = () => {
    const selectedItems = this.processTable?.selection?.selected || [];
    if (selectedItems.length === 0)
      return;
    const ids = selectedItems.map((item: any) => item.id);
    this.toastService.confirmDelete({
      title: 'delete dataset',
      content: 'do you want to delete this dataset',
      confirmText: 'delete',
      width: DIALOG_WIDTH.SMALLEST,
      onConfirm: () => {
        this.loadingService.show();
        this.processService.deleteProcess(ids).pipe(finalize(() => this.loadingService.hide())).subscribe({
          next: () => {
            this.toastService.success(this.translateService.instant('delete successfully'));
            this.retrieveData();
          },
          error: (error) => {
            console.error(error);
            this.toastService.error(this.translateService.instant(error.Message));
          }
        });
      }
    });
  };

  hasSelectedItems = (): boolean => {
    return (this.processTable?.selection?.selected?.length || 0) > 0;
  };

  getSelectionOptions(): ISelectionOption | null {
    return this.isAdmin || this.authService.userInfo?.hasPefDatabaseAccess ? { position: 0 } : null;
  }

  fetchProcesses = (params: any) => this.processService.getProcesses(params);

  retrieveData = () => this.processTable.retrieveData();

  onClickRow = ({ row: process }: any) => {
    this.router.navigate([!this.isAdmin ? ROUTES.USER.PROCESS : ROUTES.ADMIN.PROCESS, process.id]);
  };

  openImportDialog() {
    this.dialog.open(ImportProcessDialogComponent, {
      width: DIALOG_WIDTH.SMALLEST,
      disableClose: true
    }).afterClosed().subscribe((result: ImportProcessDialogResult | undefined) => {
      if (!result)
        return;
      if (result?.errors?.length) {
        this.importErrors = result.errors;
        this.scrollToImportErrors();
      }
    });
  }

  private scrollToImportErrors() {
    // scroll to the import errors section
    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      this.importErrorsSection?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  clearImportErrors = () => (this.importErrors = []);

  openAddProcessDialog() {
    this.formDialog.open({
      title: 'add dataset',
      description: 'add dataset subtitle',
      showRequiredFieldsHint: true,
      formComponent: AddDatasetFormComponent,
      cancelAction: { text: 'cancel' },
      submitAction: { text: 'add dataset' }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.retrieveData();
      }
    });
  }

  private loadFilterOptions(): void {
    const params = { page: 1, pageSize: 500, sortText: 'name.asc' };
    this.unsubscribe.push(
      forkJoin({
        dataSources: this.systemService.getDataSources(params),
        datasetUnits: this.systemService.getDatasetUnits(params)
      }).subscribe(({ dataSources, datasetUnits }) => {
        this.setFilterOptions(this.dataSourceFilterOptions, dataSources);
        this.setFilterOptions(this.datasetUnitFilterOptions, datasetUnits);
      })
    );
  }

  private setFilterOptions(target: IDataSourceFilterTable[], response: any): void {
    const options = response.data ?? response;
    target.splice(0, target.length, ...options.map((option: any) => ({ id: option.id, name: option.name })));
  }

  initColumnConfig(): IColumnConfig[] {
    return [
      {
        key: 'inline-action',
        cellStyle: { width: '50px' },
        cellDataType: TableDataType.DYNAMIC_COMPONENT,
        cellValue: ({ row }) => [
          InlineActionComponent,
          {
            row,
            layout: TableLayout.MODERN,
            dropdownIcon: 'dots-vertical',
            rightClickShowDropdown: true,
            style: { display: 'flex', justifyContent: 'center' },
            inlineActions: [
              {
                icon: !this.isAdmin ? 'eye' : 'edit',
                name: !this.isAdmin ? 'view' : 'edit',
                action: () => this.router.navigate([!this.isAdmin ? ROUTES.USER.PROCESS : ROUTES.ADMIN.PROCESS, row.id])
              },
              ...(this.isAdmin ? [{ icon: 'trash', name: 'delete', action: () => this.handleDeleteProcess(row.id) }] : [])
            ]
          }
        ]
      },
      {
        title: 'id',
        key: 'id',
        sortable: true,
        searchable: true,
        cellStyle: { width: '100px' }
      },
      {
        title: 'name',
        key: 'datasetMainName',
        sortable: true,
        searchable: true,
        cellStyle: { minWidth: '200px', paddingLeft: '16px' },
        titleStyle: { paddingLeft: '16px' },
        filterable: FilterType.TEXT
      },
      {
        title: 'data source',
        key: 'dataSource',
        filterKey: 'dataSourceId',
        sortable: true,
        searchable: true,
        cellStyle: { minWidth: '180px' },
        titleStyle: { minWidth: '180px' },
        filterable: FilterType.MULTI_SELECT,
        filterDataSource: this.dataSourceFilterOptions,
        selectionConfig: { key: 'id', title: 'name', isMultiSelect: true }
      },
      {
        title: 'dataset unit',
        key: 'datasetUnit',
        filterKey: 'datasetUnitId',
        sortable: true,
        searchable: true,
        cellStyle: { minWidth: '160px' },
        titleStyle: { minWidth: '160px' },
        filterable: FilterType.MULTI_SELECT,
        filterDataSource: this.datasetUnitFilterOptions,
        selectionConfig: { key: 'id', title: 'name', isMultiSelect: true }
      },
      {
        title: 'year of publication',
        key: 'yearOfPublication',
        sortable: true,
        searchable: true,
        filterable: FilterType.NUMBER
      },
      {
        title: 'market mix',
        key: 'marketMix',
        sortable: true,
        searchable: true,
        cellStyle: { minWidth: '120px' },
        filterable: FilterType.TEXT
      },
      {
        title: 'description',
        key: 'description',
        sortable: true,
        searchable: true,
        tooltip: true,
        cellStyle: {
          lineHeight: '1.3rem',
          maxHeight: '75px',
          width: '250px',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'normal'
        },
        filterable: FilterType.TEXT
      },
      {
        title: 'economic ef3.1',
        key: 'economicEF',
        sortable: true,
        searchable: true,
        cellStyle: { minWidth: '250px' }
      },
      {
        title: 'energy ef3.1',
        key: 'energyEF',
        sortable: true,
        searchable: true,
        cellStyle: { minWidth: '250px' }
      },
      {
        title: 'mass ef3.1',
        key: 'massEF',
        sortable: true,
        searchable: true,
        cellStyle: { minWidth: '250px' }
      },
      {
        title: 'economic recipe',
        key: 'economicRecipe',
        sortable: true,
        searchable: true,
        cellStyle: { minWidth: '250px' }
      },
      {
        title: 'energy recipe',
        key: 'energyRecipe',
        sortable: true,
        searchable: true,
        cellStyle: { minWidth: '250px' }
      },
      {
        title: 'mass recipe',
        key: 'massRecipe',
        sortable: true,
        searchable: true,
        cellStyle: { minWidth: '250px' }
      }
    ];
  }

  ngOnDestroy(): void {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}

@Component({
  imports: [MaterialModule, TranslateModule, CommonModule, LoadingIconComponent, IconModule],
  standalone: true,
  template: `
    <button class="m-b-8" mat-flat-button [disabled]="disabled() || loading" (click)="openExportProcesses()">
      <div class="d-flex align-items-center text-disable">
        <app-loading-icon [loading]="loading" />
        @if (!loading) {
          <i-tabler class="icon-20 mr-1" name="file-export" />
        }
        @if (selectedCount() > 0) {
          {{ 'export selected' | translate: { count: selectedCount() } }}
        } @else {
          {{ 'export' | translate }}
        }
      </div>
    </button>
  `
})
export class ExportProcessComponent {
  @Input() disabled = (): boolean => false;
  @Input() isAdmin: boolean = false;
  @Input() table?: () => DataTableComponent | null;

  loading = false;
  selectedCount = (): number => this.table?.()?.selection?.selected?.length ?? 0;

  constructor(
    private dialog: MatDialog,
    private processService: ProcessService) { }

  openExportProcesses() {
    const config: IConfirmDialog = {
      title: 'export process',
      content: 'content confirm export process',
      hideCancelButton: true,
      showCloseButton: true,
      bodyClass: 'pr-7!',
      onConfirm: () => this.exportProcesses()
    };

    if (!this.isAdmin) {
      const dialogRef = this.dialog.open(AppConfirmDialogComponent, {
        width: '420px',
        data: config
      });

      dialogRef.afterClosed().subscribe((confirm) => {
        confirm ? config.onConfirm() : config.onCancel?.();
      });
      return;
    }

    this.exportProcesses()
  }

  exportProcesses() {
    this.loading = true;
    const ids = this.table?.()?.selection?.selected?.map((x: any) => x.id) ?? [];
    this.processService.exportProcesses(ids).subscribe({
      next: (event: any) => {
        if (event.type !== HttpEventType.Response)
          return;

        saveAs(event.body, 'LCIA EF3.1 & ReCiPe PEF GFLI Database.xlsx');
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }
}
